/**
 * Workshop Bubble Component
 * Draggable workshop representation on plant layout
 */

import React, { useState, useRef, useCallback, useEffect } from 'react';
import { WorkshopPosition, PlantLayout, plantMappingUtils } from '../../services/plantMapping';
import { usePlantMappingStore } from '../../stores/plantMappingStore';

interface WorkshopBubbleProps {
  position: WorkshopPosition;
  layout: PlantLayout;
  isSelected: boolean;
  isDragging: boolean;
  canEditLayout?: boolean;
  onHover: (event: React.MouseEvent) => void;
  onLeave: () => void;
  onClick: () => void;
}

const WorkshopBubble: React.FC<WorkshopBubbleProps> = ({
  position,
  layout,
  isSelected,
  isDragging,
  canEditLayout = false,
  onHover,
  onLeave,
  onClick
}) => {
  const [localPosition, setLocalPosition] = useState({ x: position.x, y: position.y });
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [isLocalDragging, setIsLocalDragging] = useState(false);
  // Remove context menu states - we'll use selection-based approach instead
  const bubbleRef = useRef<SVGGElement>(null);

  const {
    setDragging,
    updatePosition,
    getZoneAtPosition,
    zones
  } = usePlantMappingStore();

  // Update local position when prop changes
  useEffect(() => {
    if (!isLocalDragging) {
      setLocalPosition({ x: position.x, y: position.y });
    }
  }, [position.x, position.y, isLocalDragging]);

  // Convert normalized coordinates to SVG coordinates
  const svgX = localPosition.x * layout.width;
  const svgY = localPosition.y * layout.height;

  // Safety check for workshop data
  if (!position.workshop) {
    console.error('WorkshopBubble: position.workshop is undefined', position);
    return null;
  }

  // Get workshop colors
  const statusColor = plantMappingUtils.getWorkshopStatusColor(position.workshop.status);
  const categoryColor = plantMappingUtils.getSQDCMCategoryColor(position.workshop.sqdcm_category);
  
  // Use category color as primary, status as border
  const bubbleColor = position.color || categoryColor;
  const borderColor = statusColor;

  // Move workshop back to unpositioned queue
  const handleMoveToUnpositioned = useCallback(async (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    
    try {
      await updatePosition(position.id, 0.05, 0.05, '');
      // Update local state to reflect unpositioned status
      setLocalPosition({ x: 0.05, y: 0.05 });
    } catch (error) {
      console.error('Failed to move workshop to unpositioned:', error);
    }
  }, [position.id, updatePosition]);

  // Handle mouse down - prepare for potential drag but don't start dragging yet
  const handleMouseDown = useCallback((event: React.MouseEvent) => {
    if (!canEditLayout) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();

    const svgElement = bubbleRef.current?.closest('svg');
    if (!svgElement) return;

    const rect = svgElement.getBoundingClientRect();
    const svgPoint = svgElement.createSVGPoint();
    svgPoint.x = event.clientX - rect.left;
    svgPoint.y = event.clientY - rect.top;
    
    const svgCoords = svgPoint.matrixTransform(svgElement.getScreenCTM()?.inverse());
    
    setDragOffset({
      x: svgCoords.x - svgX,
      y: svgCoords.y - svgY
    });
    
    // Don't start dragging immediately - wait for mouse move
    // setIsLocalDragging(true);
    // setDragging(true, position);
  }, [svgX, svgY, canEditLayout]);

  // Remove context menu effect - no longer needed

  // Handle global mouse events for drag detection and dragging
  useEffect(() => {
    let isDragStarted = false;
    let startMousePos = { x: 0, y: 0 };

    const handleMouseMove = (event: MouseEvent) => {
      // If we're already dragging, continue dragging
      if (isLocalDragging) {
        const svgElement = bubbleRef.current?.closest('svg');
        if (!svgElement) return;

        const rect = svgElement.getBoundingClientRect();
        const svgPoint = svgElement.createSVGPoint();
        svgPoint.x = event.clientX - rect.left;
        svgPoint.y = event.clientY - rect.top;
        
        const svgCoords = svgPoint.matrixTransform(svgElement.getScreenCTM()?.inverse());
        
        // Calculate new position
        const newSvgX = svgCoords.x - dragOffset.x;
        const newSvgY = svgCoords.y - dragOffset.y;
        
        // Convert to normalized coordinates
        const newX = Math.max(0, Math.min(1, newSvgX / layout.width));
        const newY = Math.max(0, Math.min(1, newSvgY / layout.height));
        
        setLocalPosition({ x: newX, y: newY });
        return;
      }

      // If mouse down happened but we haven't started dragging yet
      if (dragOffset.x !== 0 || dragOffset.y !== 0) {
        if (!isDragStarted) {
          // Store initial mouse position
          startMousePos = { x: event.clientX, y: event.clientY };
          isDragStarted = true;
          return;
        }

        // Check if mouse moved enough to start dragging (drag threshold)
        const deltaX = event.clientX - startMousePos.x;
        const deltaY = event.clientY - startMousePos.y;
        const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);
        
        if (distance > 5) { // 5px drag threshold
          // Start actual dragging
          setIsLocalDragging(true);
          setDragging(true, position);
        }
      }
    };

    const handleMouseUp = async () => {
      if (isLocalDragging) {
        // End dragging and update position
        setIsLocalDragging(false);
        setDragging(false);

        // Find zone at new position
        const zone = getZoneAtPosition(localPosition.x, localPosition.y);
        const zoneName = zone?.name || '';

        // Update position in backend
        try {
          await updatePosition(position.id, localPosition.x, localPosition.y, zoneName);
        } catch (error) {
          // Revert to original position on error
          setLocalPosition({ x: position.x, y: position.y });
          console.error('Failed to update position:', error);
        }
      } else {
        // This was just a click, not a drag - trigger selection
        if (dragOffset.x !== 0 || dragOffset.y !== 0) {
          // Reset drag offset and trigger click
          setDragOffset({ x: 0, y: 0 });
          onClick();
        }
      }
      
      // Reset drag detection state
      isDragStarted = false;
      setDragOffset({ x: 0, y: 0 });
    };

    // Only add listeners if we have drag offset (mouse was pressed)
    if (dragOffset.x !== 0 || dragOffset.y !== 0 || isLocalDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);

      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [
    isLocalDragging,
    dragOffset,
    layout,
    localPosition,
    position,
    updatePosition,
    setDragging,
    getZoneAtPosition,
    onClick
  ]);

  // Get current zone for visual feedback
  const currentZone = getZoneAtPosition(localPosition.x, localPosition.y);
  const isInZone = !!currentZone;

  // Calculate bubble size
  const bubbleSize = position.size || 24;
  const scaledSize = Math.max(12, bubbleSize * (1 + (isSelected ? 0.3 : 0) + (isLocalDragging ? 0.2 : 0)));

  return (
    <g
      ref={bubbleRef}
      className={`workshop-bubble ${isLocalDragging ? 'dragging' : ''} ${isSelected ? 'selected' : ''}`}
      transform={`translate(${svgX}, ${svgY})`}
      style={{ cursor: canEditLayout ? (isLocalDragging ? 'grabbing' : 'grab') : 'pointer' }}
      onMouseDown={canEditLayout ? handleMouseDown : (event) => event.stopPropagation()}
      onClick={canEditLayout ? undefined : (event) => {
        event.stopPropagation();
        onClick();
      }}
      onMouseEnter={onHover}
      onMouseLeave={onLeave}
    >
      {/* Zone highlight when dragging */}
      {isLocalDragging && isInZone && currentZone && (
        <circle
          r={scaledSize + 8}
          fill={currentZone.color}
          opacity="0.3"
          stroke={currentZone.border_color}
          strokeWidth="2"
          strokeDasharray="4,4"
        />
      )}

      {/* Selection ring */}
      {isSelected && (
        <circle
          r={scaledSize + 4}
          fill="none"
          stroke="#3b82f6"
          strokeWidth="3"
          opacity="0.8"
          className="animate-pulse"
        />
      )}

      {/* Main bubble */}
      <circle
        r={scaledSize}
        fill={bubbleColor}
        stroke={borderColor}
        strokeWidth="2"
        opacity={isLocalDragging ? 0.8 : 1}
        className="transition-all duration-200"
      />

      {/* Workshop number */}
      <text
        textAnchor="middle"
        dominantBaseline="central"
        fill="white"
        fontSize={Math.max(8, scaledSize * 0.4)}
        fontWeight="bold"
        style={{ pointerEvents: 'none', userSelect: 'none' }}
      >
        {position.workshop.workshop_number}
      </text>

      {/* Status indicator dot */}
      <circle
        cx={scaledSize * 0.6}
        cy={-scaledSize * 0.6}
        r="4"
        fill={statusColor}
        stroke="white"
        strokeWidth="1"
      />

      {/* Move to Queue Button (visible when selected) */}
      {canEditLayout && isSelected && !isLocalDragging && (
        <g
          className="move-to-queue-button"
          style={{ cursor: 'pointer' }}
          onClick={handleMoveToUnpositioned}
        >
          {/* Button background */}
          <rect
            x={scaledSize + 5}
            y={-10}
            width="80"
            height="20"
            fill="#f59e0b"
            stroke="#d97706"
            strokeWidth="1"
            rx="10"
            opacity="0.9"
            className="hover:opacity-100"
          />
          {/* Button text */}
          <text
            x={scaledSize + 45}
            y="0"
            textAnchor="middle"
            dominantBaseline="central"
            fill="white"
            fontSize="8"
            fontWeight="bold"
            style={{ pointerEvents: 'none', userSelect: 'none' }}
          >
            📤 Move to Queue
          </text>
        </g>
      )}

      {/* Drag handle (visible when selected) */}
      {canEditLayout && isSelected && !isLocalDragging && (
        <g opacity="0.6">
          <circle
            cx={scaledSize * 0.8}
            cy={scaledSize * 0.8}
            r="6"
            fill="white"
            stroke="#6b7280"
            strokeWidth="1"
          />
          <circle
            cx={scaledSize * 0.8}
            cy={scaledSize * 0.8}
            r="2"
            fill="#6b7280"
          />
        </g>
      )}

      {/* Zone indicator */}
      {position.zone && !isLocalDragging && (
        <text
          x="0"
          y={scaledSize + 12}
          textAnchor="middle"
          fill="#6b7280"
          fontSize="8"
          style={{ pointerEvents: 'none', userSelect: 'none' }}
        >
          {position.zone}
        </text>
      )}

      {/* Dragging feedback */}
      {isLocalDragging && (
        <>
          {/* Drop shadow */}
          <circle
            r={scaledSize}
            fill="black"
            opacity="0.2"
            transform="translate(2, 2)"
          />
          
          {/* Coordinates display */}
          <text
            x="0"
            y={scaledSize + 20}
            textAnchor="middle"
            fill="#374151"
            fontSize="10"
            fontFamily="monospace"
            style={{ pointerEvents: 'none', userSelect: 'none' }}
          >
            ({(localPosition.x * 100).toFixed(1)}%, {(localPosition.y * 100).toFixed(1)}%)
          </text>
        </>
      )}

    </g>
  );
};

export default WorkshopBubble;
